import { addToast, Button, Form, Input, Spinner } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { PbECommerceSettingsUseCaseEnum } from '@itsrever/rever-api'
import { Controller, useForm } from 'react-hook-form'
import { createSearchParams, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'
import { AxiosError } from 'axios'
import { useTranslate } from '@/hooks/useTranslate'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { parseUrlEmail } from '@/utils'
import { useOrderLite } from '@/hooks/useOrderLite'
import { useTheme } from '@itsrever/design-system'
import clsx from 'clsx'
import { useAuthenticateOrder } from '@/hooks/use-authenticate-order'
import { useTranslation } from 'react-i18next'
import { usePages, useSlug } from '@/hooks'
import {
    captureEventPosthog,
    setTagsSentry,
    setUserPosthog,
    setUserSentry
} from '@/services'
import { setOrder } from '@/redux/api/returnsApiSlice'
import {
    setEmail,
    setOrderNumber,
    setOrderProducts
} from '@/redux/order/orderSlice'
import {
    setExchangeAddress,
    setReturnAddress
} from '@/redux/return/returnSlice'
import { LoadingModal } from '@/components/Loading'
import ReverTextField from '@/components/ReverTextField'
import { redirectPath } from '@/utils/redirect'
import { resetStore } from '@/redux/store'

const schema = z.object({
    orderId: z
        .string()
        .min(1, { message: 'landing_order_number_error' })
        .trim(),
    email: z.string().email({ message: 'landing_email_error' })
})

type LoginData = z.infer<typeof schema>

export const testOrderID = 'N1234'

export const LoginForm = () => {
    const { i18n, t } = useTranslation()
    const { translate } = useTranslate()

    const dispatch = useAppDispatch()

    const theme = useTheme()

    const navigate = useNavigate()
    const { pages } = usePages()
    const [searchParams] = useSearchParams()

    const slug = useSlug()

    const emailParam = searchParams.get('email')
    const orderNumberParam = searchParams.get('orderNumber')

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const { handleStartLiteOrder } = useOrderLite()
    const { mutate, isPending } = useAuthenticateOrder()

    const {
        register,
        formState: { errors },
        control,
        setError,
        handleSubmit
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            orderId: orderNumberParam ?? '',
            email: emailParam ? parseUrlEmail(emailParam) : ''
        },
        mode: 'onChange'
    })

    const isReverLite =
        settings.use_case === PbECommerceSettingsUseCaseEnum.Lite

    const handleLogin = (values: LoginData) => {
        const { orderId, email } = values

        const customerPrintedOrderId =
            orderId === 'REVER123' ? testOrderID : orderId

        let ecommerceId = settings.ecommerce_id

        if (ecommerceId) {
            if (orderId === 'REVER123') {
                ecommerceId = 'partner123'
            }

            const redirectFrom =
                redirectPath[ecommerceId as keyof typeof redirectPath & string]

            if (redirectFrom) {
                const orderPrefix = customerPrintedOrderId
                    .substring(0, 5)
                    .toUpperCase()

                const portalPath =
                    redirectFrom[
                        orderPrefix as keyof typeof redirectFrom & string
                    ]

                if (portalPath) {
                    ecommerceId = portalPath // for this cases the slug is the same as the ecommerce id
                }
            }

            if (isReverLite) {
                handleStartLiteOrder(orderId, email)
            } else {
                mutate(
                    {
                        customerPrintedOrderId,
                        email,
                        ecommerceId,
                        userPreferredLang: i18n.language
                    },
                    {
                        onSuccess: (response) => {
                            const { order, previous_return_processes } =
                                response

                            dispatch(resetStore())

                            dispatch(
                                setOrderNumber(
                                    order?.identification
                                        ?.customer_printed_order_id
                                )
                            )
                            dispatch(setEmail(order?.customer?.email))
                            dispatch(setOrder(response))
                            dispatch(setOrderProducts(order?.line_items))

                            const address = order?.shipping_address
                            if (address) {
                                dispatch(setReturnAddress(address))
                                dispatch(setExchangeAddress(address))
                            }

                            if (!settings.is_testing) {
                                setUserPosthog(
                                    email,
                                    order?.customer?.first_name,
                                    order?.customer?.last_name
                                )
                                captureEventPosthog('Started', {
                                    orderNumber: customerPrintedOrderId
                                })
                            }

                            setUserSentry(email)
                            setTagsSentry({
                                ecommerceID: ecommerceId as string,
                                orderNumber: customerPrintedOrderId,
                                email: email,
                                portalURL: `https://returns.byrever.com/${
                                    settings.slug
                                }?orderNumber=${encodeURIComponent(
                                    customerPrintedOrderId
                                )}&email=${encodeURIComponent(email ?? '')}`
                            })

                            const isRedirectPortal =
                                redirectPath[
                                    slug as keyof typeof redirectPath & string
                                ]

                            const shouldRedirect =
                                slug &&
                                ecommerceId !== 'partner123' &&
                                isRedirectPortal

                            const hasPreviousReturns =
                                response.previous_return_processes &&
                                response.previous_return_processes.length > 0

                            if (shouldRedirect) {
                                let path = `/${ecommerceId}/order`
                                if (hasPreviousReturns) {
                                    path = `/${ecommerceId}/select`
                                }
                                navigate({
                                    pathname: path,
                                    search: createSearchParams({
                                        orderNumber: customerPrintedOrderId,
                                        email
                                    }).toString()
                                })
                                return
                            }

                            if (
                                previous_return_processes &&
                                previous_return_processes.length > 0
                            ) {
                                navigate({
                                    pathname: pages.ProcessSelection,
                                    search: createSearchParams({
                                        orderNumber: customerPrintedOrderId,
                                        email
                                    }).toString()
                                })

                                return
                            }

                            navigate({
                                pathname: pages.ItemSelection,
                                search: createSearchParams({
                                    orderNumber: customerPrintedOrderId,
                                    email
                                }).toString()
                            })
                        },
                        onError: (error) => {
                            if (error instanceof AxiosError) {
                                if (error.response?.status === 404) {
                                    setError('orderId', {
                                        message: 'landing_order_number_error'
                                    })

                                    return
                                }

                                if (error.response?.status === 503) {
                                    addToast({
                                        title: t(
                                            'toast_errors.something_went_wrong'
                                        ),
                                        description: t(
                                            'toast_errors.confirmation_503'
                                        )
                                    })

                                    return
                                }
                            }

                            addToast({
                                title: t('toast_errors.something_went_wrong'),
                                description: t('toast_errors.error_406')
                            })
                        }
                    }
                )
            }
        }
    }

    const exampleEmail = `${translate('landing_email_example')}@${
        settings.slug
    }.com`

    const color = theme.colors.primary.main
    const textColor = theme.colors.primary.contrastText
    const rounded = theme.borderRadius

    const isHoff = settings.ecommerce_group == 'hoff'
    const isMisako = settings.ecommerce_group == 'misako'

    if (isHoff) {
        return (
            <>
                <Form
                    className="w-full space-y-2 md:w-[440px]"
                    onSubmit={handleSubmit(handleLogin)}
                >
                    <Input
                        {...register('orderId')}
                        type="text"
                        placeholder="X01234"
                        radius="sm"
                        variant="underlined"
                        autoFocus
                        isInvalid={!!errors.orderId}
                        errorMessage={
                            errors.orderId?.message
                                ? translate(errors.orderId?.message)
                                : undefined
                        }
                        data-testid="orderNumber"
                        className="font-haas"
                        classNames={{
                            inputWrapper: 'border-solid'
                        }}
                    />
                    <Input
                        {...register('email')}
                        type="email"
                        placeholder={exampleEmail}
                        radius="sm"
                        size="md"
                        variant="underlined"
                        isInvalid={!!errors.email}
                        errorMessage={
                            errors.email?.message
                                ? translate(errors.email?.message)
                                : undefined
                        }
                        data-testid="email"
                        className="font-haas"
                        classNames={{
                            inputWrapper: 'border-solid'
                        }}
                    />
                    <Button
                        type="submit"
                        variant="solid"
                        radius="sm"
                        size="lg"
                        fullWidth
                        data-testid="continue"
                        style={{
                            backgroundColor: color,
                            color: textColor,
                            borderRadius: '0.5rem'
                        }}
                        isLoading={isPending}
                        spinner={<Spinner size="sm" />}
                    >
                        <p className="font-haas font-light">
                            {translate('landing_button_continue')}
                        </p>
                    </Button>
                </Form>
            </>
        )
    }

    if (isMisako) {
        return (
            <>
                {isPending ? <LoadingModal loading /> : null}
                <Form
                    className="w-full max-w-[33rem] space-y-2"
                    onSubmit={handleSubmit(handleLogin)}
                >
                    <Controller
                        control={control}
                        name="orderId"
                        render={({ field: { onChange, value } }) => (
                            <ReverTextField
                                onChange={onChange}
                                value={value}
                                type="text"
                                label={translate('landing_numorder')}
                                placeholder="X01234"
                                fullWidth
                                helperText={
                                    errors.orderId?.message
                                        ? translate(
                                              'landing_order_number_error'
                                          )
                                        : ''
                                }
                                autoFocus
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="email"
                        render={({ field: { onChange, value } }) => (
                            <ReverTextField
                                onChange={onChange}
                                value={value}
                                type="email"
                                label={translate('landing_email')}
                                placeholder={exampleEmail}
                                fullWidth
                                autoFocus
                            />
                        )}
                    />
                    <Button
                        type="submit"
                        variant="solid"
                        radius="sm"
                        size="lg"
                        fullWidth
                        data-testid="continue"
                        style={{
                            backgroundColor: color,
                            color: textColor,
                            borderRadius: '10px'
                        }}
                        isLoading={isPending}
                    >
                        <span className="text-[13px] uppercase tracking-wider [font-family:Inter]">
                            {translate('landing_button_continue')}
                        </span>
                    </Button>
                </Form>
            </>
        )
    }

    return (
        <>
            <Form
                className="w-full max-w-96 space-y-2"
                onSubmit={handleSubmit(handleLogin)}
            >
                <Input
                    {...register('orderId')}
                    type="text"
                    label={translate('landing_numorder')}
                    placeholder="X01234"
                    radius="sm"
                    variant={
                        color === 'transparent' ? 'underlined' : 'bordered'
                    }
                    autoFocus
                    isInvalid={!!errors.orderId}
                    errorMessage={
                        errors.orderId?.message
                            ? translate(errors.orderId?.message)
                            : undefined
                    }
                    data-testid="orderNumber"
                    className="font-[Roobert]"
                    classNames={{
                        inputWrapper: 'border-solid'
                    }}
                />
                <Input
                    {...register('email')}
                    type="email"
                    label={translate('landing_email')}
                    placeholder={exampleEmail}
                    radius="sm"
                    size="md"
                    variant={
                        color === 'transparent' ? 'underlined' : 'bordered'
                    }
                    isInvalid={!!errors.email}
                    errorMessage={
                        errors.email?.message
                            ? translate(errors.email?.message)
                            : undefined
                    }
                    data-testid="email"
                    className="font-[Roobert]"
                    classNames={{
                        inputWrapper: 'border-solid'
                    }}
                />
                <Button
                    type="submit"
                    variant={color === 'transparent' ? 'light' : 'solid'}
                    radius="sm"
                    size={color === 'transparent' ? 'md' : 'lg'}
                    fullWidth
                    data-testid="continue"
                    className={clsx({
                        'underline underline-offset-2 hover:!bg-neutral-100':
                            color === 'transparent'
                    })}
                    style={{
                        backgroundColor:
                            color === 'transparent' ? 'transparent' : color,
                        color: textColor,
                        borderRadius: rounded
                    }}
                    isLoading={isPending}
                    spinner={<Spinner size="sm" />}
                >
                    {translate('landing_button_continue')}
                </Button>
            </Form>
        </>
    )
}
